import React, { useState, useRef, useEffect } from 'react';
import SignaturePad from 'signature_pad';

const PickupArrivalModal = ({ 
  isOpen, 
  onClose, 
  waypoint, 
  onLoad, 
  onNoShow,
  onSignature,
  onCopayConfirm,
  onCancel 
}) => {
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [copayConfirmed, setCopayConfirmed] = useState(false);
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);

  // Get trip details from waypoint
  const trip = waypoint?.trip || {};
  const passenger = trip.passenger || {};
  const pickup = trip.pickup || {};
  const dropoff = trip.dropoff || {};
  const reservation = trip.reservation || {};

  // Check if signature or copay is required (from priceItems)
  const priceItems = reservation.priceItems || [];
  const signatureRequired = priceItems.some(item => item.Name === 'Signature') || reservation.signature === true;
  const copayItem = priceItems.find(item => item.Name === 'Copay');
  const copayRequired = copayItem || (!!reservation.copay && reservation.copay > 0);
  const copayAmount = copayItem?.TripAmount || reservation.copay || '';

  // Filter priceItems to exclude Signature and Copay for display
  const displayPriceItems = priceItems.filter(item => 
    item.Name !== 'Signature' && item.Name !== 'Copay'
  );

  // Get passenger attributes for display
  const passengerAttributes = passenger.attributes || [];

  // Helper function to format address
  const formatAddress = (location) => {
    if (!location) return 'N/A';
    
    // If landmark exists, show "Landmark Name, State"
    if (location.landmark) {
      return `${location.landmark}, ${location.state || ''}`.trim();
    }
    
    // Otherwise construct from streetNumber, streetName, city
    if (location.streetNumber && location.streetName) {
      return `${location.streetNumber} ${location.streetName}, ${location.city || ''}`.trim();
    }
    
    // Fallback to full address
    return location.address || 'N/A';
  };

  // Determine if Load button should be enabled
  const canLoad = (!signatureRequired || signatureConfirmed) && (!copayRequired || copayConfirmed);

  // Initialize signature pad when modal opens
  useEffect(() => {
    if (isOpen && canvasRef.current && signatureRequired) {
      const canvas = canvasRef.current;
      
      // Set canvas size based on container
      const resizeCanvas = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const container = canvas.parentElement;
        canvas.width = container.offsetWidth * ratio;
        canvas.height = 120 * ratio;
        canvas.style.width = `${container.offsetWidth}px`;
        canvas.style.height = '120px';
        canvas.getContext('2d').scale(ratio, ratio);
        
        if (signaturePadRef.current) {
          signaturePadRef.current.clear();
        }
      };

      signaturePadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)'
      });

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
        }
      };
    }
  }, [isOpen, signatureRequired]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSignatureConfirmed(false);
      setCopayConfirmed(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setSignatureConfirmed(false);
  };

  const handleConfirmSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const data = signaturePadRef.current.toDataURL('image/png');
      setSignatureConfirmed(true);
      
      // Send signature to server
      if (onSignature) {
        onSignature(data);
      }
    }
  };

  const handleConfirmCopay = () => {
    setCopayConfirmed(true);
    
    // Send copay confirmation to server
    if (onCopayConfirm) {
      onCopayConfirm(copayAmount);
    }
  };

  const handleLoad = () => {
    if (canLoad) {
      onLoad();
      onClose();
    }
  };

  const handleNoShow = () => {
    onNoShow();
    onClose();
  };

  // Determine header color based on seating capacity type
  const isWheelchair = reservation.wheelchair > 0 || waypoint?.wheelChair;
  const headerColor = isWheelchair ? "bg-blue-500" : "bg-orange-500";

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          // Don't close on backdrop click - user must complete action
        }
      }}
    >
      <div className="bg-white rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className={`${headerColor} text-white px-4 py-3 rounded-t-lg flex justify-between items-center`}>
          <h2 className="text-lg font-bold">Pickup</h2>
          <button
            onClick={() => {
              if (onCancel) onCancel();
              onClose();
            }}
            className="text-white hover:text-gray-200 p-1"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-2 space-y-2">
          {/* Passenger Details with Attributes and Trip Items */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Passenger</h3>
            <div className="flex gap-2">
              {/* Left: Name and Account */}
              <div className="flex-1">
                <p className="text-lg font-bold">{passenger.name || waypoint?.passengerName || 'Unknown'}</p>
                {passenger.account && (
                  <p className="text-sm text-gray-500">{passenger.account}</p>
                )}
              </div>
              
              {/* Right: Attributes and Trip Items */}
              <div className="flex-1 space-y-1">
                {/* Passenger Attributes */}
                {passengerAttributes.length > 0 && passengerAttributes[0] !== 0 && (
                  <div className="flex flex-wrap gap-1">
                    {passengerAttributes.map((attr, index) => (
                      <span 
                        key={index}
                        className="inline-block bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs"
                      >
                        {attr}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Trip Items */}
                {displayPriceItems.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {displayPriceItems.map((item, index) => (
                      <span 
                        key={index}
                        className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs"
                      >
                        {item.Name}{item.TripAmount > 0 ? `: $${item.TripAmount.toFixed(2)}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pickup Address */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Pickup</h3>
            <p className="text-sm">{formatAddress(pickup)}</p>
            {pickup.comments && (
              <p className="text-sm text-gray-600 mt-1">
                {pickup.comments}
              </p>
            )}
          </div>

          {/* Dropoff Address */}
          <div className="border-b pb-1">
            <h3 className="font-semibold text-gray-700 mb-1">Dropoff</h3>
            <p className="text-sm">{formatAddress(dropoff)}</p>
            {dropoff.comments && (
              <p className="text-sm text-gray-600 mt-1">
                {dropoff.comments}
              </p>
            )}
          </div>

          {/* Wheelchair Indicator */}
          {(reservation.wheelchair > 0 || waypoint?.wheelChair) && (
            <div className="border-b pb-1">
              <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                ♿ Wheelchair
              </span>
            </div>
          )}

          {/* Signature Section (Optional) */}
          {signatureRequired && (
            <div className="border-b pb-1">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">
                  Signature <span className="text-red-500">*</span>
                </h3>
                {signatureConfirmed && (
                  <span className="text-green-600 text-sm font-medium">✓ Confirmed</span>
                )}
              </div>
              
              {!signatureConfirmed ? (
                <>
                  <div className="border border-gray-300 rounded bg-white">
                    <canvas 
                      ref={canvasRef}
                      className="w-full touch-none"
                      style={{ height: '120px' }}
                    />
                  </div>
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={handleClearSignature}
                      className="flex-1 py-1 px-3 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleConfirmSignature}
                      className="flex-1 py-1 px-3 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      Confirm Signature
                    </button>
                  </div>
                </>
              ) : (
                <div className="border border-green-300 rounded bg-green-50 p-2 text-center">
                  <span className="text-green-700">Signature captured</span>
                </div>
              )}
            </div>
          )}

          {/* Co-pay Section (Optional) */}
          {copayRequired && copayAmount > 0 && (
            <div className="border-b pb-1">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-700">
                  Co-pay Required <span className="text-red-500">*</span>
                </h3>
                {copayConfirmed && (
                  <span className="text-green-600 text-sm font-medium">✓ Confirmed</span>
                )}
              </div>
              
              <div className={`p-3 rounded ${copayConfirmed ? 'bg-green-50 border border-green-300' : 'bg-orange-50 border border-orange-300'}`}>
                <p className="text-lg font-bold text-center">
                  ${copayAmount.toFixed(2)}
                </p>
                {!copayConfirmed && (
                  <button
                    onClick={handleConfirmCopay}
                    className="w-full mt-2 py-1 px-3 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    Confirm Co-pay Collected
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-2 pt-1">
            <button
              onClick={handleNoShow}
              className="flex-1 py-3 px-4 bg-red-500 text-white font-bold rounded hover:bg-red-600"
            >
              No Show
            </button>
            <button
              onClick={handleLoad}
              disabled={!canLoad}
              className={`flex-1 py-3 px-4 font-bold rounded ${
                canLoad 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Pickup
            </button>
          </div>

          {/* Helper text if requirements not met */}
          {!canLoad && (
            <p className="text-xs text-center text-red-500">
              {!signatureConfirmed && signatureRequired && 'Signature required. '}
              {!copayConfirmed && copayRequired && 'Co-pay confirmation required.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupArrivalModal;
